import React from "react";
import styled from "styled-components";
import {ThemeContext} from '../../../providers/theme';
import translate from '../../../providers/i18n/translate';
import ItemList from "./ItemList";
import GridViewIcon from '@mui/icons-material/GridView';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlaceIcon from '@mui/icons-material/Place';
import WebhookIcon from '@mui/icons-material/Webhook';
import LogoutIcon from '@mui/icons-material/Logout';
import { Tooltip } from "@mui/material";
import { useIntl } from 'react-intl';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../providers/authentication";
import HexagonIcon from '@mui/icons-material/Hexagon';
import Private from "../../../providers/authentication/private";
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import HorseIcon from "../../../public/iconCaballoBlanco.png";

const menuList = [
    {
        name: translate("dashboard"),
        link: "/backoffice/dashboard",
        icon: <GridViewIcon />,
    },
    {
        name: translate("users"),
        link: "/backoffice/users",
        icon: <AccountCircleOutlinedIcon />,
        scope: "users",
        private: true
    },
    {
        name: translate("horses"),
        link: "/backoffice/horses",
        icon: <img src={HorseIcon}/>
    },
    {
        name: translate("places"),
        link: "/backoffice/places",
        icon: <PlaceIcon />,
        scope: "locations",
        private: true
    },
    {
        name: translate("calendar"),
        link: "/backoffice/calendar",
        icon: <CalendarTodayIcon />
    },
    {
        name: translate("reports"),
        link: "/backoffice/reports",
        icon: <AssessmentOutlinedIcon />,
        scope: "reports",
        private: true
    },
]

const Root = styled.aside`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: ${(props) => props.openNav ? "100%" : "50px"};
    display: grid;
    grid-template-rows: 50px 1fr 50px 50px;
    overflow-y: hidden;
    background-color: ${(props) => props.bg ? props.bg : "#000"};
    transition: height 500ms ease-in-out;
    z-index: 9999;
    @media (min-width: 1024px) {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        width: ${(props) => props.openNav ? props.openWidth : props.closeWidth};
        height: 100%;
        top: inherit;
        left: inherit;
        z-index: inherit;
        overflow-x: hidden;
        overflow-y: inherit;
        grid-template-rows: inherit;
        transition: width 500ms ease-in-out;
        border-right: 1px solid ${(props) => props.border ? props.border : "transparent"};
    }
`

const Switch = styled.div`
    display: flex;
    width: 100%;
    height: 50px;
    align-items: center;
    justify-content: flex-start;
    padding: 0 12.5px;
    cursor: pointer;
    user-select: none;
    transition: all 200ms ease-in-out;
    svg {
        transition: all 200ms ease-in-out;
        color: ${(props) => props.color ? props.color : "#FFF"};
    }
    @media (min-width: 1024px) {
        svg:hover {
            transform: scale(1.1);
        }
    }
`

const MenuList = styled.ul`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    list-style: none;
    align-items: center;
    justify-content: flex-start;
    padding: 20px;
    @media (min-width: 1024px) {
        display: flex;
        padding: 0;
    }
`

const Logout = styled.div`
    display: flex;
    width: 100%;
    height: 50px;
    align-items: center;
    justify-content: flex-start;
    padding: 0 12.5px;
    cursor: pointer;
    user-select: none;
    transition: all 200ms ease-in-out;
    svg {
        transition: all 200ms ease-in-out;
        color: ${(props) => props.color ? props.color : "#FFF"};
    }
    span {
        color: ${(props) => props.color ? props.color : "#FFF"};
        text-transform: uppercase;
        transition: all 300ms ease-in-out;
        opacity: ${(props) => props.showname ? 1 : 0};
        margin: 0 0 0 10px;
    }
    @media (min-width: 1024px) {
        svg:hover {
            transform: scale(1.1);
        }
    }
`

const Mole = styled.a`
    display: flex;
    width: 100%;
    height: 60px;
    align-items: center;
    justify-content: flex-start;
    padding: 0 12.5px;
    cursor: pointer;
    user-select: none;
    transition: all 200ms ease-in-out;
    text-decoration: none;
    svg {
        height: 30px;
        width: 30px;
        transition: all 300ms ease-in-out;
        color: ${(props) => props.color ? props.color : "#FFF"};
    }
    span {
        white-space: nowrap;
        color: ${(props) => props.color ? props.color : "#FFF"};
        text-transform: uppercase;
        transition: max-width 500ms ease-in-out, opacity 700ms ease-in, margin 500ms ease-in-out;
        opacity: ${(props) => props.showname ? 1 : 0};
        max-width: ${(props) => props.showname ? "80px" : "0"};
        margin: ${(props) => props.showname ? "0 0 0 10px" : "0 0 0 0"};
        font-size: 0.6rem;
    }
    @media (min-width: 1024px) {
        svg:hover {
            transform: scale(1.1) rotate(360deg);
            color: #f3f2f9;
        }
    }
`



const AdminNav = ({openNav, onSwitchNav, closeNav}) => {

    const intl = useIntl();

    let navigate = useNavigate();

    const { onLogout } = useAuth();

    const OPEN_NAV_WIDTH = "200px";
    const CLOSE_NAV_WIDTH = "50px";

    const theme = React.useContext(ThemeContext);

    const logout = () => {
        onLogout(() => navigate("/"));
    }
    

    return (
        <Root openNav={openNav} openWidth={OPEN_NAV_WIDTH} closeWidth={CLOSE_NAV_WIDTH} bg={theme.palette.principal} border={theme.palette.accent}>
            <Switch openNav={openNav}>
                {
                    openNav ?
                    <ArrowBackIcon onClick={onSwitchNav} />
                    :
                    <MenuIcon onClick={onSwitchNav} />
                }
            </Switch>
            <MenuList openNav={false}>
                {
                    menuList && menuList.map((item, index) =>
                        {
                            return item.private ? 
                            <Private scope={item.scope} nullReturn key={`item_${index}_${item.name}`}>
                                <ItemList item={item} color={theme.palette.base} closeNav={closeNav} activecolor={theme.palette.accent} showname={openNav ? 1 : 0} />
                            </Private>
                            :
                            <ItemList key={`item_${index}_${item.name}`} item={item} color={theme.palette.base} closeNav={closeNav} activecolor={theme.palette.accent} showname={openNav ? 1 : 0} />
                        }
                    )
                }
            </MenuList>
            <Tooltip title={intl.formatMessage({ id: 'exit' })} placement={openNav ? "left" : "right"}>
                <Logout onClick={logout} showname={openNav ? 1 : 0}>
                    <LogoutIcon />
                    <span>{translate("exit")}</span>
                </Logout>
            </Tooltip>
            <Tooltip title={intl.formatMessage({ id: 'mole' })} placement={openNav ? "top" : "right"}>
                <Mole href="https://molsolutions.com.ar/" target="_blank" showname={openNav ? 1 : 0}>
                    <MoleIcon />
                    <span>Mol Solutions</span>
                </Mole>
            </Tooltip>
        </Root>
    );
}

export default AdminNav;

const MoleIcon = () => {
    return (
<svg xmlns="http://www.w3.org/2000/svg" id="Capa_2" data-name="Capa 2" viewBox="0 0 51.82 50.4">
  <g id="Capa_2-2" data-name="Capa 2">
    <path style={{fill: "currentcolor", strokeWidth: "0px"}} d="m41.23,2.56l-13.99,19.13-6.73-9.48c.3-.27.49-.65.49-1.08,0-.81-.65-1.46-1.46-1.46-.26,0-.49.07-.7.19l-3.86-5.43c.46-.4.75-.98.75-1.64,0-1.21-.98-2.19-2.19-2.19-1.13,0-2.05.86-2.16,1.96h-5.81c-.12-1.43-1.3-2.56-2.77-2.56C1.25,0,0,1.25,0,2.79s1.17,2.69,2.64,2.77v7.63c-1.14.08-2.04,1.02-2.04,2.17s.9,2.09,2.04,2.17v15.78c-1.14.08-2.04,1.02-2.04,2.17s.9,2.09,2.04,2.17v7.18c-1.47.08-2.64,1.28-2.64,2.77s1.25,2.79,2.79,2.79,2.65-1.13,2.77-2.56h5.81c.11,1.1,1.03,1.96,2.16,1.96,1.21,0,2.19-.98,2.19-2.19,0-1.13-.86-2.05-1.96-2.16v-5.79c.91-.11,1.63-.88,1.63-1.83s-.71-1.71-1.63-1.83v-3.72c.91-.11,1.63-.88,1.63-1.83s-.71-1.71-1.63-1.83v-4.56c.24-.03.46-.1.66-.21l3.76,5.23c-.38.34-.62.83-.62,1.37,0,1.02.83,1.85,1.85,1.85.33,0,.64-.09.91-.25l3.25,4.52h6.52l10.09-13.01v24.72h11.64V2.56h-10.59Zm-19.99,27.78c-.06-.9-.75-1.62-1.64-1.71l-.31-9.64c.07-.01.13-.03.19-.06l5.58,10.74c-.28.21-.49.51-.56.86l-3.26-.19ZM11.84,4.15l-2.24,2.22c-.32-.25-.71-.4-1.14-.4-.47,0-.89.18-1.21.46l-2.22-2c.3-.4.48-.89.53-1.42h5.81c.04.43.21.82.47,1.14Zm7.35,8.39l-.29,3.55c-.32.02-.61.15-.84.34l-2.74-2.35c.25-.35.39-.78.39-1.25,0-.13-.02-.26-.04-.38l2.53-.72c.19.4.55.7.98.81Zm6.43,16.87c-.06.01-.11.03-.16.05l-5.58-10.74c.19-.14.34-.33.45-.54l4.79,1.46.51,9.77Zm-17.57,9.64c-.33,0-.64.12-.89.31l-2.66-2.54c.29-.37.47-.83.47-1.34,0-.25-.05-.49-.13-.72l7.12-3.34c.13.2.29.38.49.52l-3.91,7.19c-.15-.06-.32-.09-.49-.09Zm3.73-8.03l-7.13,3.34c-.18-.3-.43-.55-.73-.74l3.53-7.36c.19.06.38.1.59.1.4,0,.77-.13,1.08-.35l3,3.25c-.27.32-.44.73-.44,1.19,0,.2.04.39.1.57Zm-3.73-8.35c-.27,0-.52.06-.75.16l-3.36-5.62c.18-.12.35-.26.49-.42l7.43,4.67c-.11.24-.18.5-.18.78,0,.17.03.34.08.49l-2.11.88c-.32-.56-.91-.94-1.6-.94Zm5.26-7.66v5.4c-.49.06-.91.3-1.2.67l-7.42-4.65c.18-.32.29-.68.29-1.06,0-.11-.02-.21-.03-.31l6.53-1.5c.27.79.98,1.37,1.84,1.46Zm-1.94-1.9l-6.54,1.5c-.12-.33-.31-.62-.56-.85l3.27-4.35c.27.16.58.25.92.25.43,0,.82-.15,1.14-.4l2.24,2.22c-.3.38-.49.85-.49,1.37,0,.09.02.18.03.27ZM4.74,4.78l2.21,1.98c-.21.3-.34.66-.34,1.06,0,.52.22.99.57,1.33l-3.27,4.35c-.25-.15-.53-.25-.83-.29v-7.66c.64-.07,1.22-.34,1.65-.77Zm-1.2,12.64l3.38,5.66c-.43.34-.72.86-.72,1.45,0,.65.34,1.23.85,1.55l-3.53,7.36c-.14-.05-.29-.09-.44-.11v-15.81c.16-.02.31-.06.45-.11Zm.66,19.73l2.66,2.54c-.17.24-.26.52-.26.83,0,.4.16.77.43,1.03l-2.72,3.72c-.36-.23-.77-.37-1.21-.42v-7.21c.42-.06.8-.23,1.11-.5Zm.45,8.4l2.73-3.74c.2.1.43.17.67.17.26,0,.49-.07.7-.19l3.27,4.24c-.37.35-.6.82-.66,1.36h-5.81c-.06-.73-.39-1.38-.91-1.84Zm7.73.21l-3.27-4.24c.25-.26.4-.61.4-1,0-.16-.03-.3-.08-.44l2.53-1.27c.29.46.77.78,1.34.85v5.79c-.34.03-.65.14-.93.31Zm-.7-7.92c0,.2.04.39.1.56l-2.54,1.27c-.08-.12-.19-.22-.3-.31l3.92-7.19c.14.06.3.09.46.11v3.72c-.91.11-1.63.88-1.63,1.83Zm.77-8.87l-3-3.25c.27-.32.44-.73.44-1.19,0-.17-.03-.34-.08-.49l2.11-.88c.28.5.78.85,1.38.92v4.56c-.32.04-.61.15-.85.33Zm2.32-8.08c-.28-.25-.63-.42-1.02-.47v-5.4c.49-.05.94-.26,1.28-.58l2.74,2.35c-.14.22-.22.49-.22.77s.08.54.22.77l-3,2.57Zm10.07-1.79l-4.39-1.34c0-.07.02-.14.02-.21,0-.69-.48-1.26-1.12-1.42l.29-3.55c.17-.01.34-.05.49-.12l4.72,6.64Zm-6.37-8.97c-.25.26-.4.61-.4,1,0,.06.01.12.02.18l-2.53.72c-.32-.8-1.11-1.37-2.03-1.37-.53,0-1,.19-1.38.5l-2.24-2.22c.24-.31.39-.7.39-1.13s-.15-.82-.39-1.13l2.24-2.22c.38.31.85.5,1.38.5.39,0,.75-.11,1.07-.29l3.87,5.44Zm-3.69,13.47c.37-.34.6-.82.6-1.36,0-.38-.12-.74-.31-1.03l3-2.56c.21.18.48.3.77.34l.31,9.64c-.22.03-.42.1-.61.2l-3.76-5.23Zm6.43,7.19l3.26.19c.04.47.31.88.7,1.11l-1.55,3.8-2.95-4.1c.28-.26.47-.61.54-1.01Z"/>
  </g>
</svg>
    )
}